import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * Floating "Update available" banner.
 *
 * Listens for the `sw-update-available` window event dispatched from
 * `index.js` when a new service worker has installed and is waiting.
 * Clicking Reload sends `SKIP_WAITING` to the worker; the page then auto-
 * reloads (handled by `controllerchange` in `index.js`) once the new
 * worker takes over.
 */
const SwUpdateBanner = () => {
  const [waiting, setWaiting] = useState(null);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    const onUpdate = (e) => {
      const w = e?.detail?.worker;
      if (w) setWaiting(w);
    };
    window.addEventListener('sw-update-available', onUpdate);
    return () => window.removeEventListener('sw-update-available', onUpdate);
  }, []);

  if (!waiting) return null;

  const handleReload = () => {
    setReloading(true);
    try {
      waiting.postMessage('SKIP_WAITING');
    } catch {
      window.location.reload();
    }
  };

  return (
    <div
      className="fixed bottom-20 sm:bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 px-4 py-3 rounded-xl border border-indigo-500/50 bg-slate-900/95 backdrop-blur-md shadow-2xl shadow-indigo-900/40 animate-[fadeIn_0.25s_ease-out]"
      role="status"
      data-testid="sw-update-banner"
    >
      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-400/40">
        <RefreshCw className={`w-4 h-4 text-indigo-300 ${reloading ? 'animate-spin' : ''}`} />
      </span>
      <div className="flex flex-col">
        <p className="text-sm font-semibold text-white leading-tight">
          New version available
        </p>
        <p className="text-xs text-gray-400 leading-tight mt-0.5">
          Reload to get the latest fixes & features.
        </p>
      </div>
      <button
        onClick={handleReload}
        disabled={reloading}
        className="ml-2 px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 rounded-lg disabled:opacity-50 transition-colors"
        data-testid="sw-update-reload-btn"
      >
        {reloading ? 'Reloading…' : 'Reload'}
      </button>
      <button
        onClick={() => setWaiting(null)}
        className="text-gray-500 hover:text-gray-300 text-xs px-1"
        title="Dismiss"
        data-testid="sw-update-dismiss-btn"
      >
        Later
      </button>
    </div>
  );
};

export default SwUpdateBanner;
